/**
 * Voice Task Service
 * Both transcription and AI analysis go through the Laravel backend
 * (server-to-server), exactly like growthAiService.js — no CORS/auth issues.
 *
 * Laravel routes needed:
 *   POST api/ai-transcribe  → AiTranscribeController@transcribe  (new)
 *   POST api/ai-proxy       → AiProxyController@proxy            (already exists)
 */
import Vue from 'vue';

/**
 * Transcribe an audio Blob using Whisper-1 via the Laravel backend.
 * Laravel forwards the file to the Whisper Cloud Function server-to-server.
 *
 * @param {Blob} audioBlob
 * @returns {Promise<string>} Transcribed text
 */
export async function transcribeAudio(audioBlob) {
    const ext = audioBlob.type.includes('mp4') ? 'mp4'
        : audioBlob.type.includes('wav') ? 'wav'
        : audioBlob.type.includes('ogg') ? 'ogg'
        : 'webm';

    const formData = new FormData();
    formData.append('file', audioBlob, `recording.${ext}`);
    formData.append('language', 'en');
    // Prompt primes Whisper for Irish-English phonetics, accent patterns and vocabulary.
    // Whisper treats this as preceding context, biasing its decoder toward these word forms.
    // ─── Phonetic notes (for Whisper token bias) ───────────────────────────────────────
    // VOWELS: Irish English has distinct vowel realisations:
    //   • /aɪ/ (PRICE) → closer to "oy" or "oi": "toime" (time), "loike" (like), "roight" (right)
    //   • /eɪ/ (FACE) → monophthong "eh": "teh" (take), "meh" (make), "deh" (day)
    //   • /oʊ/ (GOAT) → "owe" or "oh": "gowan" (go on), "show" stays "show"
    //   • /ɪ/ (KIT) → sometimes "ee": "git" can sound like "geet"
    //   • NURSE vowel /ɜː/ → unrounded: "wark" (work), "furst" (first), "thurd" (third)
    //   • Unstressed vowels often reduced: "the" → "deh", "to" → "teh", "of" → "uh"
    // CONSONANTS:
    //   • "th" /θ/ → "t" or "d": "tree" (three), "dat" (that), "dem" (them), "dere" (there)
    //   • "th" /ð/ → "d": "dis" (this), "dose" (those), "dese" (these)
    //   • Final "-ing" → "-in'": "runnin", "workin", "doin"
    //   • "-tion" suffix → "-shun" (standard) or sometimes "-chun"
    //   • "wh-" → "w": "where" sounds like "were", "what" like "wut"
    //   • Intervocalic /t/ stays crisp (not flapped like American English): "butter", "water"
    //   • Final /t/ often unreleased or glottalised: "what" → "wha'", "that" → "tha'"
    //   • /r/ is rhotic (pronounced after vowels): "car", "worker", "here" all have audible /r/
    //   • "can't" → sounds like "kant" (no trap-bath split)
    // PROSODY / RHYTHM:
    //   • Sentences often rise at the end (high rising terminal), even in statements
    //   • Speech can be fast with clipped syllables
    //   • "I will" → "I will" (not contracted to "I'll" in speech)
    //   • "I am" → "I am" (not "I'm") in emphatic contexts
    //   • "ye" used instead of "you" (plural and singular)
    //   • "youse" → plural "you"
    // ─── Vocabulary ────────────────────────────────────────────────────────────────────
    formData.append('prompt', 'Irish English speaker. Task management. Phonetic patterns: "th" sounds like "d" or "t" (dat=that, dere=there, tree=three, ting=thing). Final -ing drops g (runnin, workin). Vowel shifts: toime=time, loike=like, roight=right, wark=work. Rhotic r always pronounced. Fast rhythm, high rising intonation. Common Irish words: grand, sorted, deadly, fierce, class, gas, sound, craic, lash, manky, buzzing, savage, bang on, suss out, yoke, eejit, gobshite, cop on, banjaxed, fluthered, langers, scarlet, mortified, kip, gaff, mot, messing, gowan=go on, ya=you, ye=you, youse=you all, wha=what, tha=that, goin=going, doin=doing. Irish task phrases: "would ya ever", "right so", "ah sure", "jaysus", "lookit", "I want ya to", "would ya not", "go on so", "fair enough", "how are ya", "any craic". Task context: assign task, due date, priority, deadline, project, meeting, report, review.');

    const res = await Vue.axios.post('api/ai-transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    const text = typeof res.data === 'string' ? res.data : (res.data?.text || '');
    if (!text) throw new Error('No transcription returned. Please try again.');
    return text.trim();
}

/**
 * Analyze a transcription and extract structured task intent using the Responses API.
 *
 * @param {string} transcription - The text to analyze
 * @param {Array} users - List of users [{ id, name, email? }] for assignee matching
 * @param {Array} companies - List of companies [{ id, name }] for company matching
 * @param {string} todayDate - ISO date string for today (YYYY-MM-DD)
 * @param {Array} roles - List of roles/departments [{ id, name }] from api/users/get_user_roles
 * @returns {Promise<object>} Parsed task object:
 *   { task_type: 'self'|'assign', name, description, due_date, priority_id, assignee_ids, company_id }
 */
export async function analyzeTaskIntent(transcription, users = [], companies = [], todayDate = null, roles = []) {
    const today = todayDate || (() => {
        // Use local date to avoid UTC off-by-one in non-UTC timezones
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    })();

    // Pre-compute common relative dates so AI gets exact values, not guesses
    const addDays = (base, n) => {
        const d = new Date(base + 'T12:00:00'); // noon avoids DST shift
        d.setDate(d.getDate() + n);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const nextWeekday = (base, targetDay) => { // 0=Sun,1=Mon...6=Sat
        const d = new Date(base + 'T12:00:00');
        const current = d.getDay();
        const diff = (targetDay - current + 7) % 7 || 7;
        d.setDate(d.getDate() + diff);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const endOfMonth = (base) => {
        const d = new Date(base + 'T12:00:00');
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()}`;
    };
    const todayWeekday = new Date(today + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
    const dateMap = {
        tomorrow: addDays(today, 1),
        'next monday': nextWeekday(today, 1),
        'next tuesday': nextWeekday(today, 2),
        'next wednesday': nextWeekday(today, 3),
        'next thursday': nextWeekday(today, 4),
        'next friday': nextWeekday(today, 5),
        'next saturday': nextWeekday(today, 6),
        'next sunday': nextWeekday(today, 0),
        'end of week': nextWeekday(today, 5),
        'next week': nextWeekday(today, 1),
        'end of month': endOfMonth(today),
        'in 2 days': addDays(today, 2),
        'in 3 days': addDays(today, 3),
        'in 7 days': addDays(today, 7),
        'in 2 weeks': addDays(today, 14),
    };
    const dateExamples = Object.entries(dateMap)
        .map(([expr, val]) => `  "${expr}" → ${val}`)
        .join('\n');

    // Build rich user list — include role_id so AI can cross-reference the roles list
    const userList = users.slice(0, 100).map(u => {
        const roleName = roles.find(r => r.id === u.role_id)?.name || u.position_name || u.role?.name || null;
        return `- ID ${u.id}: ${u.name}${u.email ? ` <${u.email}>` : ''}${roleName ? ` | dept/role: ${roleName}` : ''}`;
    }).join('\n');

    // Build department → user IDs map using the authoritative roles list from the API
    const deptMap = {};
    users.forEach(u => {
        // Prefer role name from the roles API over user object fields
        const roleName = (roles.find(r => r.id === u.role_id)?.name || u.position_name || u.role?.name || '').toLowerCase().trim();
        if (roleName) {
            if (!deptMap[roleName]) deptMap[roleName] = [];
            deptMap[roleName].push(u.id);
        }
    });
    const deptList = Object.entries(deptMap)
        .map(([dept, ids]) => `- "${dept}" → user IDs: [${ids.join(', ')}]`)
        .join('\n');

    // Also list all known roles/departments from the API (even if no users assigned yet)
    const rolesList = roles.map(r => `- ID ${r.id}: ${r.name}`).join('\n');

    const companyList = companies.slice(0, 50).map(c => `- ID ${c.id}: ${c.name}`).join('\n');

    const systemPrompt = `You are an expert task management assistant. Your job is to parse a voice command into a structured task object. Be intelligent and generous in your interpretation — extract as much useful information as possible.

Today's date: ${today} (${todayWeekday})

Available users (ID | name | dept/role):
${userList || '(no users available)'}

All departments/roles in the system (from API):
${rolesList || '(no roles available)'}

Department → User ID mapping (resolved from API roles):
${deptList || '(no department members available)'}

Available companies:
${companyList || '(no companies available)'}

Priority system:
- 1 = Urgent  (use for: ASAP, urgent, critical, emergency, immediately, right now, fire, blocker)
- 2 = High    (use for: important, high priority, soon, need this done, end of day, today)
- 3 = Medium  (use for: normal tasks with no special urgency, this week, a few days)
- 4 = Low     (use for: whenever, no rush, low priority, someday, nice to have, backlog)

Respond with ONLY a valid JSON object — no markdown, no explanation, no extra text:
{
  "task_type": "self" or "assign",
  "name": "primary task title (used when tasks array has only 1 item, max 100 chars)",
  "description": "expanded description or null",
  "due_date": "YYYY-MM-DD or null",
  "priority_id": 1|2|3|4,
  "assignee_ids": [array of integer user IDs] or [],
  "role_id": integer role/department ID or null,
  "company_id": integer company ID or null,
  "tasks": ["task name 1", "task name 2", ...] — array of task titles when multiple tasks are detected, otherwise ["single task name"]
}

=== MULTI-TASK DETECTION ===
If the voice command mentions multiple distinct tasks or a list of things to do, return ALL of them in the "tasks" array:
- "Create tasks for updating the website, fixing the login bug, and sending the weekly report" → tasks: ["Update website", "Fix login bug", "Send weekly report"]
- "I need to do three things: call the client, update the spreadsheet, and book the meeting room" → tasks: ["Call client", "Update spreadsheet", "Book meeting room"]
- "Assign Dylan to fix the API, update the docs, and review the pull request" → tasks: ["Fix API", "Update documentation", "Review pull request"]
- Signals for multiple tasks: "and also", "as well as", "plus", "three things", "a few tasks", "list of", comma-separated actions
- Each task name should be concise and action-oriented (max 80 chars)
- Shared fields (assignee_ids, role_id, priority_id, due_date, company_id) apply to ALL tasks in the array
- When only ONE task is detected, tasks array still has one element and name = that element

=== TASK TYPE RULES ===
- "self" → user says: "I need to", "remind me", "my task", "I have to", "for me", "I should", "create a task to"
- "assign" → user mentions someone else's name OR a department/team name, says "assign to", "delegate to", "tell [name/dept] to", "ask [name] to", "[name] should", "get [dept] to", "have the [dept] team", or assignee_ids is non-empty

=== TASK NAME RULES ===
- Extract the core action as a clean, imperative sentence (e.g. "Prepare Q2 sales report", "Fix login bug")
- Remove filler words like "um", "uh", "like", "so"
- Do NOT include names, dates, or priority words in the title — those go in other fields
- Max 100 characters

=== DESCRIPTION RULES ===
- If the voice command contains details, steps, context, or requirements beyond the title → put them here as a clean paragraph
- If the user explicitly described what the task involves (e.g. "make sure it includes X, Y, Z") → include those as bullet points or a short paragraph
- If the command is brief and the title already captures everything → set to null
- NEVER duplicate the task name verbatim in the description
- Write in clear, professional English

=== PRIORITY RULES ===
You MUST always set a priority_id. Never return null for priority. Use every available signal to infer the best fit:
- Explicit urgency words → Urgent (1): "ASAP", "urgent", "critical", "emergency", "immediately", "right now", "fire", "blocker", "drop everything", "fierce urgent", "deadly urgent"
- Time pressure today → High (2): "end of day", "EOD", "today", "by close of business", "COB", "before I leave", "this afternoon", "this morning", "in the next hour", "fierce important", "very important", "really important"
- Near-term deadline → High (2): "tomorrow", "by tomorrow", "first thing tomorrow"
- Within the week → Medium (3): "this week", "by Friday", "in a few days", "next couple of days", "soon", "when you get a chance today", tasks that involve reporting, reviewing, or preparing documents for an upcoming event
- No time signal, standard task → Medium (3): regular operational tasks like "create", "prepare", "update", "send", "check", "review", "follow up", "schedule", "organise" with no urgency — DEFAULT to Medium (3)
- Low urgency explicit → Low (4): "no rush", "whenever", "low priority", "someday", "nice to have", "backlog", "if you get time", "eventually"
When in doubt, default to Medium (3). NEVER return null.

=== DUE DATE RULES ===
Always convert date expressions to an exact YYYY-MM-DD. Use the pre-computed values below — do NOT recalculate:
${dateExamples}

For other expressions:
- "today" → ${today}
- "this [weekday]" → use the same value as "next [weekday]" if that day hasn’t passed yet this week
- "in X days" → add X to today
- "in X weeks" → add X*7 to today
- Specific date ("May 15th", "15th of May") → compute YYYY-MM-DD using the current year (${today.slice(0, 4)}) unless a different year is stated
- If no date is mentioned → null

=== ROLE ASSIGNMENT RULES ===
For every assignment task, you MUST determine the best role_id to assign the task under. Use the "All departments/roles" and "Department → User ID mapping" lists above:
- When assigning to a specific person (e.g. Dillon), look up their dept/role from the user list and set role_id to the matching role from the roles list
- When a person belongs to multiple roles or the name is ambiguous, pick the role that best fits the task context (e.g. Dillon in "IT Developers" for a technical task, "Marketing" for a marketing task)
- When assigning to a department/team directly (e.g. "IT Developers"), set role_id to that department's ID
- If a person has a clear single role, always use it
- role_id must be an integer from the "All departments/roles" list — never invent one
- If truly no role can be determined, set to null

=== USER / COMPANY MATCHING ===
- Match names case-insensitively and by partial match (e.g. "John" matches "John Smith")
- If ambiguous (multiple matches), pick the closest match or include all plausible IDs
- Match company names case-insensitively
- If no match found → leave as empty array or null

=== DEPARTMENT ASSIGNMENT RULES ===
When the speaker refers to a department, team, or role group instead of a specific person, use the Department → User ID mapping above to resolve ALL members:
- "IT department" / "IT team" / "the IT guys" → find all users where dept/role contains "IT" → include ALL their IDs in assignee_ids
- "dev team" / "developers" / "development" → find users with matching dept/role
- "sales team" / "marketing" / "operations" / "HR" / "finance" / "support" → same pattern
- "management" / "managers" / "leadership" → find users with manager/lead/head in their role
- Partial department name matches are valid: "IT" matches "IT Support", "IT Manager", "IT Developer"
- If multiple departments are mentioned, include IDs from all of them
- Department assignment → task_type must be "assign"
- If department reference is ambiguous, err on the side of including more users

=== IRISH ENGLISH DIALECT ===
The speaker is Irish. Interpret these expressions correctly when building the task:
- "grand" → fine / acceptable / no urgency
- "sorted" → resolved / completed / handle it
- "deadly" / "class" / "savage" / "buzzing" → excellent / great (positive)
- "fierce" → very / extremely (e.g. "fierce important" = very important → High priority)
- "your man" / "your woman" → refers to a specific person (match from user list by context)
- "give out" → complain / reprimand — not about giving something
- "on the blink" / "banjaxed" → broken / not working
- "in bits" → in a bad state / overwhelmed
- "cop on" → use common sense
- "yoke" → thing / device / object
- "lookit" / "right so" / "ah sure" → conversational filler, ignore for task content
- "I want ya to" / "would ya" → assignment directive → task_type = "assign"
- "the craic" → fun / news / situation (context-dependent)
- "lash" → attempt / go at something quickly
- "manky" → dirty / broken / bad quality
- "press" → cupboard / storage
- "gaff" → house / office / place
- "eejit" / "spa" → foolish person (informal, ignore unless relevant)
- "scarlet" / "mortified" → embarrassed
- "kip" → sleep or a messy place
- "bang on" → exactly right / on time
- "suss out" → investigate / figure out
- "stories" (as greeting) → ignore, conversational only
- "immersion" → water heater (Irish household context)
Always interpret intent charitably — Irish speech is often indirect. E.g. "Would ya ever send that report to John" = assign report task to John.

=== IRISH PHONETIC TRANSCRIPTION CORRECTIONS ===
The transcription may contain phonetic artifacts from an Irish accent. Automatically correct these before parsing:

CONSONANT SHIFTS (common Whisper errors with Irish speakers):
- "dat" / "deh" / "d'" → "that"
- "dere" / "der" → "there"
- "dem" → "them"
- "dis" → "this"
- "dose" / "dese" → "those" / "these"
- "tree" (when not a plant) → "three"
- "ting" / "t'ing" → "thing"
- "wit" (at end of phrase) → "with"
- "wha" / "wha'" → "what"
- "tha'" / "tha" → "that"
- "meself" → "myself"
- "yerself" / "yaself" → "yourself"
- "d'ya" / "dya" / "dyah" → "do you"
- "wouldja" / "would'ja" → "would you"

VOWEL SHIFTS (Whisper may transcribe Irish vowels differently):
- "toime" → "time"
- "loike" / "loik" → "like"
- "roight" / "righ'" → "right"
- "noight" → "night"
- "moind" → "mind"
- "wark" → "work"
- "furst" → "first"
- "thurd" → "third"
- "gowan" / "go wan" → "go on" (often means "proceed" or dismissal)
- "awright" → "alright"

DROPPED LETTERS / CONTRACTIONS:
- "runnin" / "workin" / "doin" / "goin" / "talkin" → add the "g" back (-ing)
- "ya" → "you" (second person singular)
- "ye" → "you" (singular or plural)
- "youse" → "you all" / "everyone"
- "I'll" often spoken as "I will" — treat both as same
- "I'm" often spoken as "I am" — treat both as same
- "c'mere" / "come here" → attention-getter, means "listen" not a location command
- "g'wan" → "go on" / "really?" / "proceed"

TASK-SPECIFIC PHONETIC PATTERNS:
- "rep-or'" / "repor'" → "report"
- "mee'in" / "meetin" → "meeting"
- "presen-ation" → "presentation"
- "dead-line" spoken fast → "deadline"
- Numbers: "t'ree" = 3, "forr-teen" = 14, "tirty" = 30, "tirty-tree" = 33
- Days: "Mondeh" = Monday, "Frideh" = Friday, "Saturdeh" = Saturday
When a word is ambiguous, pick the interpretation that makes most sense in a task management context.`;

    const payload = {
        model: 'gpt-5.4-mini',
        input: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `Voice command: "${transcription}"` },
        ],
    };

    // Route through Laravel api/ai-proxy — same as growthAiService.js askAI()
    const res = await Vue.axios.post('api/ai-proxy', payload);

    // OpenAI Responses API: output[0].content[0].text
    let rawText = res.data?.output?.[0]?.content?.[0]?.text || '';
    if (!rawText && typeof res.data === 'string') rawText = res.data;

    // Strip ```json fences if present
    rawText = rawText.replace(/^```json\s*/i, '').replace(/\s*```$/, '').trim();

    try {
        const parsed = JSON.parse(rawText);
        return {
            task_type: parsed.task_type === 'self' ? 'self' : 'assign',
            name: String(parsed.name || (parsed.tasks?.[0]) || '').slice(0, 120),
            description: parsed.description || null,
            due_date: parsed.due_date || null,
            priority_id: parsed.priority_id ? Number(parsed.priority_id) : null,
            assignee_ids: Array.isArray(parsed.assignee_ids) ? parsed.assignee_ids.map(Number) : [],
            role_id: parsed.role_id ? Number(parsed.role_id) : null,
            company_id: parsed.company_id ? Number(parsed.company_id) : null,
            tasks: Array.isArray(parsed.tasks) && parsed.tasks.length > 0
                ? parsed.tasks.map(t => String(t).slice(0, 100))
                : null,
        };
    } catch {
        throw new Error('Could not parse AI response. Please try again.');
    }
}
